import { useContext, useEffect, useState, useMemo, useCallback } from "react";
// import useScript from "hooks/useScript";
import { TOContext } from "context/TContext";
import { useLocation } from "react-router-dom";
import { loadScript } from "helpers/loadScript";
import { loadStripe } from "@stripe/stripe-js";
import { marketplaceTipOptions } from "components/Payments/config";
import { resolvePromotionDetailsFromOrder } from "helpers/promotionCharges";
import {
  PAYMENT_SYSTEM_TYPES,
  PaymentSystemContext,
} from "context/PaymentSystemContext";
import {
  useLazyGetOrderDetailQuery,
  useGetPaymentSystemQuery,
  useLazyGetStripeAccountQuery,
} from "api";

// Custom hook for URL parameters
const useUrlParams = () => {
  const location = useLocation();

  return useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      bId: searchParams.get("bId"),
      oId: searchParams.get("oId"),
      isDriverParam: searchParams.get("driver") === "true",
    };
  }, [location.search]);
};

// Custom hook for payment system initialization
const usePaymentSystemInit = (paymentSystemData, bId) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [stripeInstance, setStripeInstance] = useState(null);
  const [getStripeAccount] = useLazyGetStripeAccountQuery();
  const { setPaymentSystem } = useContext(PaymentSystemContext);

  // Initialize Forward Payment
  const initializeForwardPayment = useCallback(async () => {
    try {
      const result = await loadScript(
        process.env.REACT_APP_FORWARD_SDK_URL || "",
      );
      setLoaded(result?.loaded || false);
      setError(result?.error || false);
    } catch (error) {
      console.error("Error initializing Forward payment:", error);
      setLoaded(false);
      setError(true);
    }
  }, []);

  // Initialize Stripe Payment
  const initializeStripePayment = useCallback(
    async (paymentSystemData) => {
      try {
        const result = await getStripeAccount(bId);
        const isStripeEnabled =
          paymentSystemData?.paymentSystem === PAYMENT_SYSTEM_TYPES.STRIPE;
        const accountId = result?.data?.accountId;

        if (!isStripeEnabled || !accountId) {
          console.warn("Stripe payment not properly configured");
          return;
        }

        const PUBLIC_KEY = process.env.REACT_APP_API_REACT_APP_STRIPE_KEY;

        if (!PUBLIC_KEY) {
          console.error("Stripe public key not found");
          setError(true);
          return;
        }
        const stripe = await loadStripe(PUBLIC_KEY, {
          stripeAccount: accountId,
        });

        if (stripe) {
          setStripeInstance(stripe);
          setLoaded(true);
          setError(false);
        } else {
          throw new Error("Failed to load Stripe");
        }
      } catch (error) {
        console.error("Error initializing Stripe payment:", error);
        setLoaded(false);
        setError(true);
      }
    },
    [bId, getStripeAccount],
  );

  const initializeAdyenPayment = useCallback(async () => {
    setLoaded(true);
    setError(false);
  }, []);

  // Initialize payment system when data is available
  const initializePaymentSystem = useCallback(async () => {
    if (!paymentSystemData?.paymentSystem) return;

    // Reset loading states
    setLoaded(false);
    setError(false);

    try {
      switch (paymentSystemData.paymentSystem) {
        case PAYMENT_SYSTEM_TYPES.FORWARD:
          setPaymentSystem(PAYMENT_SYSTEM_TYPES.FORWARD);
          await initializeForwardPayment();
          break;
        case PAYMENT_SYSTEM_TYPES.STRIPE:
          setPaymentSystem(PAYMENT_SYSTEM_TYPES.STRIPE);
          await initializeStripePayment(paymentSystemData);
          break;
        case PAYMENT_SYSTEM_TYPES.ADYEN:
          setPaymentSystem(PAYMENT_SYSTEM_TYPES.ADYEN);
          await initializeAdyenPayment();
          break;
        default:
          setPaymentSystem(null);
          console.warn(
            "Unknown payment system:",
            paymentSystemData.paymentSystem,
          );
          setError(true);
      }
    } catch (error) {
      console.error("Error initializing payment system:", error);
      setError(true);
    }
  }, [
    paymentSystemData,
    setPaymentSystem,
    initializeForwardPayment,
    initializeStripePayment,
    initializeAdyenPayment,
  ]);

  // Effect to initialize payment system
  useEffect(() => {
    if (paymentSystemData?.paymentSystem) {
      initializePaymentSystem();
    }
  }, [paymentSystemData?.paymentSystem, initializePaymentSystem]);

  return { loaded, error, stripeInstance };
};

// Custom hook for order details
const useOrderDetails = (bId, oId, paymentSystemData) => {
  const [loading, setLoading] = useState(false);
  const [getOrderDetail] = useLazyGetOrderDetailQuery();

  const {
    setBusiness,
    isPaid,
    setIsPaid,
    setCharges,
    setAggregatedTipOptions,
    setOrderType,
    setIsDriver,
    setMerchantSettings,
    setPaidTip,
  } = useContext(TOContext);

  const { isDriverParam } = useUrlParams();

  // Handle driver parameter
  useEffect(() => {
    if (isDriverParam) {
      setIsDriver(true);
    }
  }, [isDriverParam, setIsDriver]);

  // Fetch order details
  const fetchOrderDetails = useCallback(async () => {
    if (!bId || !oId || !paymentSystemData?.paymentSystem) return;

    setLoading(true);

    // Helper that triggers the lazy query and returns the raw orderData
    // (or null if the response was empty). `forceRefetch` bypasses the
    // RTK Query cache so a stale/empty cached entry can't keep us pinned
    // to the empty state on refresh.
    const requestOrderData = async () => {
      const response = await getOrderDetail({ oId }, false);
      if (response?.error) {
        throw response.error;
      }
      return response?.data?.data ?? null;
    };

    try {
      let orderData = await requestOrderData();

      // Defensive retry: on refresh we have occasionally seen the
      // response come back without a body (e.g. browser cache returning
      // a stripped 304 to fetch). Retrying once with a short backoff
      // resolves it and avoids the "have to refresh twice" symptom.
      if (!orderData) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        orderData = await requestOrderData();
      }

      if (!orderData) {
        console.error(
          "Order details response did not contain a payload after retry",
          { oId },
        );
        return;
      }
      const business = orderData.metadata?.business;

      setPaidTip(orderData?.metadata?.paidTip ?? 0);

      // Store data in localStorage
      localStorage.setItem("businessId", business?.id);
      localStorage.setItem(`orderNumber-${oId}`, orderData.orderNumber);

      // Set business data
      setBusiness(business || null);
      setMerchantSettings(business?.settings?.merchantSettings || null);

      // Process tip options if business data is available
      if (business?.settings) {
        const options = business.settings;
        const businessTipOpts = options?.businessTipOptions || {};
        const groupTipOpts = options?.businessGroupTipOptions || {};

        setAggregatedTipOptions({
          kitchenTip:
            businessTipOpts?.kitchenTip ??
            groupTipOpts?.kitchenTip ??
            marketplaceTipOptions.kitchenTip,
          defaultSelectedKitchenIndex:
            businessTipOpts?.defaultSelectedKitchenIndex ??
            groupTipOpts?.defaultSelectedKitchenIndex ??
            marketplaceTipOptions.defaultSelectedKitchenIndex,
          driverTip:
            businessTipOpts?.driverTip ??
            groupTipOpts?.driverTip ??
            marketplaceTipOptions.driverTip,
          defaultSelectedDriverIndex:
            businessTipOpts?.defaultSelectedDriverIndex ??
            groupTipOpts?.defaultSelectedDriverIndex ??
            marketplaceTipOptions.defaultSelectedDriverIndex,
        });
      }

      // Set charges with properly formatted tip info
      setCharges({
        ...orderData.charges,
        promotionDetails: resolvePromotionDetailsFromOrder(orderData),
        promotionDiscount: Number(orderData.charges?.promotionDiscount || 0),
        discountDetails: orderData.charges?.discountDetails || [],
        tipInfo: {
          kitchen: Number(
            (orderData.charges?.tipInfo?.kitchen || 0) -
              Number(orderData?.metadata?.paidTip || 0),
          ),
          driver: Number(orderData.charges?.tipInfo?.driver || 0),
        },
      });

      setOrderType(orderData.orderType);

      // Set paid status based on order state and balance
      const isPaidStatus = Boolean(
        orderData.state?.name === "PAID" &&
        orderData.charges?.balanceDue != null &&
        orderData.charges.balanceDue <= 0,
      );
      setIsPaid(isPaidStatus);
    } catch (err) {
      console.error("Failed to fetch order details:", err);
    } finally {
      setLoading(false);
    }
  }, [
    bId,
    oId,
    paymentSystemData?.paymentSystem,
    getOrderDetail,
    setBusiness,
    setAggregatedTipOptions,
    setCharges,
    setOrderType,
    setIsPaid,
    setMerchantSettings,
  ]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  return { loading, isPaid };
};

// Custom hook for WebView handling
const useWebView = (isPaid, error) => {
  const [isWebView, setIsWebView] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsWebView(navigator.userAgent.includes("WebView"));
    }
  }, []);

  useEffect(() => {
    if (isWebView && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ isPaid, error }));
    }
  }, [isPaid, isWebView, error]);

  return { isWebView };
};

// Main useAppConfig hook
const useAppConfig = () => {
  const { bId, oId } = useUrlParams();

  // Get payment system data
  const { data: paymentSystemData } = useGetPaymentSystemQuery(
    { businessId: bId },
    {
      skip: !bId,
    },
  );

  // Use specialized hooks
  const { loaded, error, stripeInstance } = usePaymentSystemInit(
    paymentSystemData,
    bId,
  );
  const { loading, isPaid } = useOrderDetails(bId, oId, paymentSystemData);
  const { isWebView } = useWebView(isPaid, error);

  return {
    loaded,
    error,
    loading,
    isWebView,
    bId,
    oId,
    isPaid,
    paymentSystemData,
    stripeInstance,
  };
};

export default useAppConfig;
